import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { progressApi, type UpsertProgressInput } from '../../../services/progress';
import type { ProgressResponse } from '@xdface-vault/shared';

// Query keys
export const progressKeys = {
  all: ['progress'] as const,
  lists: () => [...progressKeys.all, 'list'] as const,
  list: (status?: string) => [...progressKeys.lists(), { status }] as const,
  detail: (type: string, slug: string) => [...progressKeys.all, 'detail', type, slug] as const,
};

/**
 * Query hook for listing progress items, auto-refreshes every 5 minutes
 */
export function useProgressList(status?: string) {
  return useQuery({
    queryKey: progressKeys.list(status),
    queryFn: () => progressApi.listProgress(status ? { status } : undefined),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Query hook for fetching detail of a single progress item
 */
export function useProgressDetail(type: string, slug: string) {
  return useQuery({
    queryKey: progressKeys.detail(type, slug),
    queryFn: () => progressApi.getProgressBySlug(type, slug),
    staleTime: 1 * 60 * 1000, // 1 minute
    enabled: !!type && !!slug,
  });
}

/**
 * Mutation hook for creating/updating progress with optimistic updates
 */
export function useUpsertProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpsertProgressInput) => progressApi.upsertProgress(input),

    onMutate: async (newItem) => {
      // Cancel all progress list queries to avoid conflicts
      await queryClient.cancelQueries({ queryKey: progressKeys.lists() });

      // Snapshot all list caches before mutating
      const snapshots: Map<readonly unknown[], ProgressResponse[]> = new Map();
      const queries = queryClient.getQueriesData<ProgressResponse[]>({
        queryKey: progressKeys.lists(),
      });

      for (const [key, data] of queries) {
        if (data) {
          snapshots.set(key, data);

          // Find existing item in current cache data
          const existingItem = data.find(
            (item) => item.type === newItem.type && item.slug === newItem.slug,
          );

          // Build the optimistic item, preserving fields from existing cache entry
          const optimisticItem: ProgressResponse = {
            type: newItem.type,
            slug: newItem.slug,
            title: newItem.title,
            currentPage: newItem.currentPage ?? existingItem?.currentPage,
            totalPages: newItem.totalPages ?? existingItem?.totalPages,
            completedLessons: newItem.completedLessons ?? existingItem?.completedLessons,
            totalLessons: newItem.totalLessons ?? existingItem?.totalLessons,
            status: newItem.status ?? existingItem?.status ?? 'in-progress',
            percentComplete: computePercent(newItem),
            rating: newItem.rating ?? existingItem?.rating,
            startedAt:
              existingItem?.startedAt ??
              (newItem.status === 'in-progress' ? new Date().toISOString() : undefined),
            completedAt:
              newItem.status === 'completed'
                ? (existingItem?.completedAt ?? new Date().toISOString())
                : existingItem?.completedAt,
            updatedAt: new Date().toISOString(),
          };

          // Merge: replace if exists, else prepend
          const existingIdx = data.findIndex(
            (item) => item.type === newItem.type && item.slug === newItem.slug,
          );
          const mergedItem = { ...existingItem, ...optimisticItem };
          const next =
            existingIdx >= 0
              ? data.map((item, i) => (i === existingIdx ? mergedItem : item))
              : [mergedItem, ...data];

          queryClient.setQueryData(key, next);
        }
      }

      return { snapshots };
    },

    onError: (_err, _input, context) => {
      // Rollback all list caches on failure
      if (context?.snapshots) {
        for (const [key, data] of context.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
    },

    onSettled: () => {
      // Always invalidate to sync with server
      queryClient.invalidateQueries({ queryKey: progressKeys.lists() });
    },
  });
}

// Helper to compute optimistic percentComplete
function computePercent(input: UpsertProgressInput): number {
  if (input.currentPage !== undefined && input.totalPages) {
    return Math.min(100, Math.round((input.currentPage / input.totalPages) * 100));
  }
  if (input.completedLessons !== undefined && input.totalLessons) {
    return Math.min(100, Math.round((input.completedLessons / input.totalLessons) * 100));
  }
  if (input.status === 'completed') return 100;
  return 0;
}
